import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, use, useCallback, useMemo, useSyncExternalStore, type ReactNode } from 'react';

import * as authApi from '@/api/auth.api';
import type { MeResult } from '@/api/types';
import { isApiError } from '@/lib/errors';
import { disablePush } from '@/lib/push';
import { queryKeys } from '@/lib/query-keys';
import { tokenStore } from '@/lib/token-store';
import { AccessRefusedError, counterAccess } from './access';
import type { Permission } from './permissions';

type AuthContextValue = {
  user: MeResult | null;
  permissions: ReadonlySet<string>;
  /** True while the session is being restored from a stored refresh token. */
  isLoading: boolean;
  isAuthenticated: boolean;
  signIn: (credentials: { email: string; password: string }) => Promise<void>;
  signOut: () => Promise<void>;
  can: (permission: Permission) => boolean;
  canAny: (permissions: readonly Permission[]) => boolean;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  /*
   * On a page reload there is no access token in memory, only a refresh token in
   * storage. Rather than mint one eagerly, we let the /auth/me request 401 and
   * the client's refresh interceptor recover it - one code path for expiry and
   * for cold start, so there is no second implementation to keep in step.
   */
  const hasRefreshToken = useSyncExternalStore(
    tokenStore.subscribe,
    () => tokenStore.getRefreshToken() !== null,
  );

  const meQuery = useQuery({
    queryKey: queryKeys.me,
    queryFn: authApi.me,
    enabled: hasRefreshToken,
    staleTime: 5 * 60_000,
    retry: (failureCount, error) => (isApiError(error) && error.status < 500 ? false : failureCount < 2),
  });

  const signInMutation = useMutation({
    /*
     * The password is checked by the server; who this app is FOR is checked
     * here, before any token is kept. An account with no branch is refused on
     * the login page rather than let into an app with nothing to show, and the
     * session the server just opened is revoked so it does not linger.
     */
    mutationFn: async (credentials: { email: string; password: string }) => {
      const result = await authApi.login(credentials);
      const access = counterAccess(result.user);
      if (access !== 'ok') {
        await authApi.logout(result.refreshToken).catch(() => undefined);
        throw new AccessRefusedError(access);
      }
      return result;
    },
    onSuccess: (result) => {
      tokenStore.set({ accessToken: result.accessToken, refreshToken: result.refreshToken });
      // Seed rather than invalidate: login already returned the user, so a
      // second round-trip before the first render would be wasted.
      queryClient.setQueryData(queryKeys.me, {
        ...result.user,
        permissions: [],
      } satisfies MeResult);
      void queryClient.invalidateQueries({ queryKey: queryKeys.me });
    },
  });

  const signIn = useCallback(
    async (credentials: { email: string; password: string }) => {
      await signInMutation.mutateAsync(credentials);
    },
    [signInMutation],
  );

  const signOut = useCallback(async () => {
    const refreshToken = tokenStore.getRefreshToken();
    /*
     * Drop this device's push subscription FIRST, while the token that
     * authorises the call still exists. A counter tablet handed to the next
     * shift must not go on buzzing for whoever was signed in before - and on
     * a shared device that is the normal case, not the exception.
     */
    await disablePush();
    tokenStore.clear();
    queryClient.clear();
    // Best-effort: the local session is already gone, so a failed revoke must
    // not strand the user on a page they can no longer load.
    if (refreshToken) await authApi.logout(refreshToken).catch(() => undefined);
  }, [queryClient]);

  const user = meQuery.data ?? null;

  const permissions = useMemo<ReadonlySet<string>>(
    () => new Set(user?.permissions ?? []),
    [user?.permissions],
  );

  const value = useMemo<AuthContextValue>(() => {
    const can = (permission: Permission) => permissions.has(permission);
    return {
      user,
      permissions,
      isLoading: hasRefreshToken && meQuery.isPending,
      isAuthenticated: user !== null,
      signIn,
      signOut,
      can,
      canAny: (list) => list.some(can),
    };
  }, [user, permissions, hasRefreshToken, meQuery.isPending, signIn, signOut]);

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const context = use(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}
